// Reglas puras de un cobro/abono en dinero, compartidas por el cobro de ventas (AccountEntry) y el
// cobro de mesas (PaymentEntryForm): formas de pago, cuenta de tesorería por tipo, voucher y
// comprobante, propina y cambio. Espejo de lo que valida el servidor
// (backend/services/payments.registerPayment); adelantarlo evita el viaje y dice QUÉ campo corregir.
import { round2, resolveSimpleEntry } from '../components/Accounting/accountEntryRules';

// Catálogo del core (PaymentMethod.MONEY_CHOICES sin «Mixto», que es un reparto, no una forma).
export const PAY_METHODS = [
    { value: 'CASH', label: 'Efectivo', short: 'Efectivo' },
    { value: 'TRANSFER', label: 'Transferencia', short: 'Transf.' },
    { value: 'CHECK', label: 'Cheque', short: 'Cheque' },
    { value: 'DEBIT_CARD', label: 'Tarjeta de Débito', short: 'Débito' },
    { value: 'CREDIT_CARD', label: 'Tarjeta de Crédito', short: 'Crédito' },
];

// Las del mostrador de un restaurante, en su orden (sin cheque: pendiente #16 del doc de mesas).
export const SALON_METHODS = ['CASH', 'DEBIT_CARD', 'CREDIT_CARD', 'TRANSFER']
    .map(value => PAY_METHODS.find(m => m.value === value));

export function methodLabel(code) {
    const method = PAY_METHODS.find(m => m.value === code);
    return method ? method.label : (code || '');
}

export const isCard = (method) => method === 'DEBIT_CARD' || method === 'CREDIT_CARD';
export const takesProof = (method) => method === 'TRANSFER' || method === 'CHECK';

// Tipo de cuenta de tesorería (FinantialAccount.moneyType) que recibe cada forma de pago.
export function moneyTypeFor(method) {
    if (method === 'CASH') return 'CASH';
    if (isCard(method)) return 'CARD';
    return 'TRANSFER';
}

export function accountsFor(accounts, method) {
    const type = moneyTypeFor(method);
    return (accounts || []).filter(a => a.moneyType === type);
}

// Cuenta preseleccionada: la única compatible; en efectivo, la primera (la caja).
export function defaultAccountId(accounts, method) {
    const list = accountsFor(accounts, method);
    if (list.length === 1 || (method === 'CASH' && list.length)) return list[0].id;
    return null;
}

export function tipFromPercent(base, percent) {
    return round2((Number(base) || 0) * (Number(percent) || 0) / 100);
}

// Cambio a entregar en efectivo; null si no se ingresó lo recibido. Negativo = falta dinero.
export function cashChange(received, amount, tip) {
    if (received === null || received === undefined || received === '') return null;
    return round2(Number(received) - (Number(amount) || 0) - (Number(tip) || 0));
}

// ¿Por qué no se puede registrar este pago? {field, message} o null. `p`: {amount, due, method,
// accountId, approvalNumber, proof, tip, received}; `setup`: lo que el servidor decidió
// (requiresAccount, requiresProof, canCollect).
export function paymentError(p, setup) {
    const s = setup || {};
    const amount = round2(p.amount);
    if (s.canCollect === false) {
        return { field: 'collect-permission', message: 'Sin permiso para cobrar: esta cuenta la cobra caja.' };
    }
    if (!p.method) return { field: 'method', message: 'Elige la forma de pago.' };
    if (!(amount > 0)) return { field: 'amount', message: 'El monto tiene que ser mayor a cero.' };
    if (amount > round2(p.due)) {
        return { field: 'amount', message: `El pago ($${amount.toFixed(2)}) supera lo que falta ($${round2(p.due).toFixed(2)}).` };
    }
    const tip = Number(p.tip) || 0;
    if (tip < 0) return { field: 'tip', message: 'La propina no puede ser negativa.' };
    if (!p.accountId && s.requiresAccount) {
        return { field: 'account', message: 'Elige la cuenta donde entra el pago.' };
    }
    if (!p.accountId && ((p.approvalNumber || '').trim() || p.proof)) {
        return { field: 'account', message: 'El voucher y el comprobante se guardan en la cuenta donde entra el pago: elige la cuenta.' };
    }
    if (p.accountId && isCard(p.method) && !(p.approvalNumber || '').trim()) {
        return { field: 'approval', message: 'Ingresa el número de aprobación del voucher.' };
    }
    if (takesProof(p.method) && s.requiresProof && !p.proof) {
        return { field: 'proof', message: `Adjunta el comprobante de ${p.method === 'CHECK' ? 'cheque' : 'la transferencia'}.` };
    }
    // Lo que de verdad entra (el efectivo recibido, o el valor cargado en el voucher o la
    // transferencia) se ingresa siempre y cubre el pago MÁS la propina: la misma regla que el cobro
    // de una venta (`resolveSimpleEntry`). Sin cambio en tarjeta o transferencia.
    const charged = resolveSimpleEntry({ total: amount, paymentCode: p.method, inputAmount: p.received, tip });
    const withTip = round2(amount + round2(tip)).toFixed(2);
    if (charged.error === 'amount-required') {
        return { field: 'received', message: p.method === 'CASH'
            ? 'Ingresa cuánto se recibe en efectivo.'
            : `Ingresa el valor cobrado ($${withTip} con la propina).` };
    }
    if (charged.error === 'cash-insufficient') {
        return { field: 'received', message: `Lo recibido no alcanza: faltan $${Math.abs(charged.change).toFixed(2)} (pago + propina).` };
    }
    if (charged.error === 'excess-must-be-tip') {
        return { field: 'received', message: `El valor cobrado supera el pago y la propina ($${withTip}): regístralo como propina o corrígelo.` };
    }
    if (charged.error === 'amount-mismatch') {
        return { field: 'received', message: `El valor cobrado tiene que ser $${withTip} (pago + propina).` };
    }
    return null;
}
